import { useState, useRef, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Copy,
  Database,
  Globe,
  Wrench,
  Bot,
  Search,
  MessageSquare,
  Save,
  RotateCcw,
  Loader2,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { CATEGORY_INFO, orderGroups, groupToolNames } from '@/lib/utils/toolGroups'
import {
  usePlaygroundMcpServers,
  useTogglePlaygroundMcpServer,
  useCustomSystemPrompt,
  useSetCustomSystemPrompt,
  useToolCatalog,
  useToolDefaults,
  useSetToolDefaults,
  type PlaygroundMcpServer,
} from '@/lib/api/hooks'
import { ToolSelectorDialog } from '@/components/chat/ToolSelectorDialog'

// Rich metadata for system MCP servers
const MCP_SERVER_META: Record<
  string,
  {
    icon: typeof Globe
    tools: string[]
    useCases: string[]
  }
> = {
  'EcoSemantic Agents': {
    icon: Bot,
    tools: ['scout_agent', 'analyst_agent', 'tracer_agent', 'modeler_agent', 'reporter_agent'],
    useCases: [
      'Run multi-step LCA workflows with a single prompt',
      'Get structured reports with interpretation and recommendations',
      'Trace supply chains and identify environmental hotspots',
    ],
  },
  'Web Search': {
    icon: Search,
    tools: ['web_search_preview', 'web_fetch'],
    useCases: [
      'Look up emission factors and technical specs from the web',
      'Cross-reference LCA results with published literature',
      'Retrieve product datasheets for custom modeling inputs',
    ],
  },
}

interface Tool {
  name: string
  databases: string[]
  database_independent: boolean
}

interface TierWithTools {
  tier_id: number
  tier_name: string
  is_default: boolean
  tools: Tool[]
  databases: string[]
  tool_count: number
  database_count: number
}

interface AvailableToolsTabProps {
  tiersData: TierWithTools[]
}

export function AvailableToolsTab({ tiersData }: AvailableToolsTabProps) {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['core'])
  const { data: mcpServers } = usePlaygroundMcpServers()
  const toggleMutation = useTogglePlaygroundMcpServer()
  const pendingRef = useRef(false)
  const [togglingUuid, setTogglingUuid] = useState<string | null>(null)

  // Default tool template
  const [defaultsOpen, setDefaultsOpen] = useState(false)
  const { data: catalog } = useToolCatalog()
  const { data: toolDefaults } = useToolDefaults()
  const setDefaults = useSetToolDefaults()

  // Custom system prompt
  const { data: promptData } = useCustomSystemPrompt()
  const setPromptMutation = useSetCustomSystemPrompt()
  const [promptDraft, setPromptDraft] = useState<string | null>(null)
  const defaultPrompt = promptData?.default_system_prompt ?? ''
  const savedPrompt = promptData?.custom_system_prompt ?? null
  const promptValue = promptDraft ?? savedPrompt ?? defaultPrompt
  const isDirty = promptDraft !== null && promptDraft !== (savedPrompt ?? defaultPrompt)
  const isCustomized = savedPrompt !== null
  const maxLength = promptData?.max_length ?? 5000

  const handleSavePrompt = useCallback(() => {
    const value = promptDraft?.trim() || null
    setPromptMutation.mutate(value, {
      onSuccess: () => setPromptDraft(null),
    })
  }, [promptDraft, setPromptMutation])

  const handleDiscardDraft = useCallback(() => {
    setPromptDraft(null)
  }, [])

  const handleResetToDefault = useCallback(() => {
    setPromptMutation.mutate(null, {
      onSuccess: () => setPromptDraft(null),
    })
  }, [setPromptMutation])

  const handleMcpToggle = (server: PlaygroundMcpServer) => {
    if (!server.can_toggle || pendingRef.current) return
    pendingRef.current = true
    setTogglingUuid(server.uuid)
    toggleMutation.mutate(
      { serverUuid: server.uuid, enabled: !server.is_enabled },
      {
        onSettled: () => {
          pendingRef.current = false
          setTogglingUuid(null)
        },
      },
    )
  }

  const optionalServers = mcpServers?.filter((s) => !s.is_default) ?? []
  const alwaysOnServers =
    mcpServers?.filter((s) => s.is_default && s.name !== 'EcoSemantic LCA') ?? []

  // Collect all tier tool names across tiers.
  const allTools = useMemo(() => {
    const s = new Set<string>()
    tiersData.forEach((tier) => tier.tools.forEach((t) => s.add(t.name)))
    return s
  }, [tiersData])

  // The backend catalog is the single source of truth for per-tool group +
  // description. The grid shows tier tools that are present in the catalog
  // (tier ∩ catalog); a tier tool absent from the catalog is not shown.
  const catalogByName = useMemo(
    () => new Map((catalog?.tools ?? []).map((t) => [t.name, t] as const)),
    [catalog],
  )
  const toolsByCategory = useMemo(
    () => groupToolNames(allTools, catalogByName),
    [allTools, catalogByName],
  )
  const categories = useMemo(
    () => orderGroups(toolsByCategory.keys()),
    [toolsByCategory],
  )
  const shownCount = useMemo(
    () => [...toolsByCategory.values()].reduce((n, arr) => n + arr.length, 0),
    [toolsByCategory],
  )

  // Get databases from highest tier (all available). Independent of the catalog.
  const sortedTiers = [...tiersData].sort((a, b) => b.tier_id - a.tier_id)
  const databases = sortedTiers[0]?.databases || []

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    )
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card>
          <CardHeader>
            <CardTitle>Available Tools</CardTitle>
            <CardDescription>
              {catalog
                ? `All ${shownCount} tools are unlocked. Each tool call costs 1 credit.`
                : 'Loading tools…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!catalog ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                <span className="text-sm">Loading tools…</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {categories.map((cat) => {
                  const tools = toolsByCategory.get(cat) || []
                  if (tools.length === 0) return null
                  const info = CATEGORY_INFO[cat]
                  return (
                    <div key={cat} className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold">{tools.length}</div>
                      <div className="text-xs text-muted-foreground">
                        {info?.title ?? cat}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {databases.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Databases ({databases.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {databases.map((db: string) => (
                    <Badge
                      key={db}
                      variant="secondary"
                      className="text-xs cursor-pointer hover:bg-secondary/80"
                      onClick={() => {
                        navigator.clipboard.writeText(db)
                        toast.success(`Copied: ${db}`)
                      }}
                    >
                      {db}
                      <Copy className="w-2.5 h-2.5 ml-1 opacity-50" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Default Tool Template */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium">Default tools for new conversations</h3>
            <p className="text-xs text-muted-foreground">
              Core tools are always included. New conversations start from this set; you can adjust
              per conversation before starting.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDefaultsOpen(true)}>
            <Wrench className="w-4 h-4 mr-1" /> Edit defaults
          </Button>
        </div>
      </div>
      {catalog && (
        <ToolSelectorDialog
          open={defaultsOpen}
          onOpenChange={setDefaultsOpen}
          catalog={catalog.tools}
          value={toolDefaults?.enabled_tools ?? []}
          coreLocked
          title="Default tools for new conversations"
          onSave={(enabled) => setDefaults.mutate(enabled)}
        />
      )}

      {/* MCP Servers */}
      {(optionalServers.length > 0 || alwaysOnServers.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                MCP Servers
              </CardTitle>
              <CardDescription>
                Additional MCP servers available in the playground. Enable them to extend your AI
                assistant's capabilities.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alwaysOnServers.map((server) => {
                  const meta = MCP_SERVER_META[server.name]
                  const Icon = meta?.icon || Globe
                  return (
                    <div
                      key={server.uuid}
                      className="p-3 rounded-lg border bg-success-muted/30 border-success/10"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="font-medium text-sm">{server.name}</span>
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 text-success border-success/30"
                            >
                              Always Active
                            </Badge>
                          </div>
                          {server.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                              {server.description}
                            </p>
                          )}
                        </div>
                      </div>
                      {meta && (
                        <div className="mt-2.5 ml-6 space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {meta.tools.map((tool) => (
                              <Badge
                                key={tool}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0 font-mono"
                              >
                                {tool}
                              </Badge>
                            ))}
                          </div>
                          <ul className="space-y-0.5">
                            {meta.useCases.map((uc, i) => (
                              <li
                                key={i}
                                className="text-xs text-muted-foreground flex items-start gap-1.5"
                              >
                                <span className="text-primary/60 mt-0.5 shrink-0">•</span>
                                {uc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )
                })}
                {optionalServers.map((server) => {
                  const meta = MCP_SERVER_META[server.name]
                  const Icon = meta?.icon || Globe
                  return (
                    <div
                      key={server.uuid}
                      className={cn(
                        'p-3 rounded-lg border transition-all',
                        server.is_enabled
                          ? 'bg-success-muted/30 border-success/10'
                          : 'bg-muted/30 border-muted',
                        !server.can_toggle && 'opacity-50',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="font-medium text-sm">{server.name}</span>
                            {server.is_enabled && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 text-success border-success/30"
                              >
                                Active
                              </Badge>
                            )}
                            {!server.can_toggle && server.required_feature && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                Requires {server.required_feature}
                              </Badge>
                            )}
                          </div>
                          {server.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                              {server.description}
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={server.is_enabled}
                          onCheckedChange={() => handleMcpToggle(server)}
                          disabled={!server.can_toggle || togglingUuid === server.uuid}
                          className="data-[state=checked]:bg-primary shrink-0 ml-3"
                        />
                      </div>
                      {meta && (
                        <div className="mt-2.5 ml-6 space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {meta.tools.map((tool) => (
                              <Badge
                                key={tool}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0 font-mono"
                              >
                                {tool}
                              </Badge>
                            ))}
                          </div>
                          <ul className="space-y-0.5">
                            {meta.useCases.map((uc, i) => (
                              <li
                                key={i}
                                className="text-xs text-muted-foreground flex items-start gap-1.5"
                              >
                                <span className="text-primary/60 mt-0.5 shrink-0">•</span>
                                {uc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tools by Category */}
      {!catalog ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          <span className="text-sm">Loading tools…</span>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((category, catIdx) => {
            const tools = toolsByCategory.get(category) || []
            if (tools.length === 0) return null

            const isExpanded = expandedCategories.includes(category)
            const categoryInfo = CATEGORY_INFO[category]

            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIdx * 0.1 }}
              >
                <Card>
                  <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(category)}>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="hover:bg-muted/50 transition-colors cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            )}
                            <div className="text-left">
                              <CardTitle className="text-lg">
                                {categoryInfo?.title ?? category}
                              </CardTitle>
                              <CardDescription>{categoryInfo?.description}</CardDescription>
                            </div>
                          </div>
                          <Badge variant="secondary">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {tools.length} tools
                          </Badge>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <Separator className="mb-4" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[...tools]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((tool) => (
                              <Tooltip key={tool.name}>
                                <TooltipTrigger asChild>
                                  <div
                                    className="p-3 rounded-lg border bg-success-muted/30 border-success/10 hover:border-success/30 transition-all cursor-pointer"
                                    onClick={() => {
                                      navigator.clipboard.writeText(tool.name)
                                      toast.success(`Copied: ${tool.name}`)
                                    }}
                                  >
                                    <div className="flex items-start justify-between mb-1.5">
                                      <code className="text-sm font-mono font-medium">
                                        {tool.name}
                                      </code>
                                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                          1 credit
                                        </Badge>
                                        <CheckCircle className="w-3.5 h-3.5 text-success" />
                                      </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {tool.description}
                                    </p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Click to copy tool name</p>
                                </TooltipContent>
                              </Tooltip>
                            ))}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Custom System Prompt */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              System Prompt
            </CardTitle>
            <CardDescription>
              {isCustomized
                ? 'You have a custom system prompt. The AI assistant uses this instead of the default.'
                : 'This is the default system prompt used by the AI assistant. Edit it to customize behavior.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={promptValue}
              onChange={(e) => setPromptDraft(e.target.value)}
              placeholder="System prompt..."
              className="min-h-[200px] resize-y text-sm font-mono"
              maxLength={maxLength}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">
                {promptValue.length}/{maxLength} characters
                {isCustomized && <span className="ml-2 text-amber-500">(customized)</span>}
              </span>
              <div className="flex gap-2">
                {isDirty && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDiscardDraft}
                    className="text-xs h-8"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Discard
                  </Button>
                )}
                {isCustomized && !isDirty && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetToDefault}
                    disabled={setPromptMutation.isPending}
                    className="text-xs h-8"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Reset to Default
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSavePrompt}
                  disabled={!isDirty || setPromptMutation.isPending}
                  className="text-xs h-8"
                >
                  <Save className="w-3 h-3 mr-1" />
                  {setPromptMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
